import React, { useState } from 'react';
import { colors } from '../theme/colors';
import { typography } from '../theme/typography';
import { spacing } from '../theme/spacing';

interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label: string;
    options: SelectOption[];
    error?: string;
    helperText?: string;
    placeholder?: string;
}

export const Select: React.FC<SelectProps> = ({
    label,
    options,
    error,
    helperText,
    placeholder,
    disabled,
    style,
    onFocus,
    onBlur,
    id,
    value,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const selectId = id || props.name || label.replace(/\s+/g, '-').toLowerCase();

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[8],
        marginBottom: spacing[24],
    };

    const labelStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        fontWeight: typography.weights.medium,
        color: colors.text.primary,
        lineHeight: typography.sizes.small.lineHeight,
    };

    const selectStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        color: value ? colors.text.primary : colors.text.muted,
        backgroundColor: '#FFFFFF',
        // Synced refined border styles with Input
        border: error
            ? `1px solid ${colors.status.error}`
            : isFocused
                ? `1px solid ${colors.primary.navy}`
                : `1px solid ${isHovered ? 'rgba(10, 38, 71, 0.3)' : 'rgba(10, 38, 71, 0.1)'}`,
        borderRadius: '8px',
        padding: `${spacing[12]} ${spacing[16]}`,
        // Extra padding right for the arrow
        paddingRight: spacing[48],
        outline: 'none',
        boxShadow: isFocused
            ? `0 0 0 3px ${error ? 'rgba(196, 69, 54, 0.1)' : 'rgba(10, 38, 71, 0.1)'}`
            : 'none',
        transition: 'all 0.2s ease',
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        // Updated chevron to be slightly sharper/darker to match text
        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5%207l5%205%205-5%22%20stroke%3D%22%23213448%22%20stroke-width%3D%222%22%20fill%3D%22none%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 1rem center',
        backgroundSize: '1.25rem',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        color: error ? colors.status.error : colors.text.muted,
        marginTop: spacing[4],
    };

    return (
        <div style={containerStyle}>
            <label htmlFor={selectId} style={labelStyle}>
                {label}
            </label>
            <select
                id={selectId}
                disabled={disabled}
                value={value}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onFocus={(e) => {
                    setIsFocused(true);
                    onFocus?.(e);
                }}
                onBlur={(e) => {
                    setIsFocused(false);
                    onBlur?.(e);
                }}
                style={selectStyle}
                {...props}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {(error || helperText) && (
                <span style={helperStyle}>{error || helperText}</span>
            )}
        </div>
    );
};
